import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import IconContainer from "@/components/ui/icon-container";
import Text from "@/components/ui/text";
import {
  CarbonCopy,
  Check,
  ChevronDown,
  ChevronUp,
  Signature
} from "@dreamhub/designsystem/icons";
import { cn } from "@/utils/utils";
import { ProposalRecipientRole } from "../types";
import { RECIPIENT_ROLES } from "./recipients";

const ROLE_ICONS: Record<ProposalRecipientRole, typeof Signature> = {
  [ProposalRecipientRole.SIGNER]: Signature,
  [ProposalRecipientRole.COPY]: CarbonCopy
};

interface Props {
  role: ProposalRecipientRole;
  // Every signature place is taken, so this recipient can't become a signer.
  signerUnavailableReason?: string;
  onChange: (role: ProposalRecipientRole) => void;
  "aria-label": string;
  "data-test"?: string;
}

export const RecipientRoleSelect: React.FC<Props> = ({
  role,
  signerUnavailableReason,
  onChange,
  "aria-label": ariaLabel,
  "data-test": dataTest
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger
        aria-label={ariaLabel}
        data-test={dataTest}
        className={cn(
          "flex items-center gap-2 w-[200px] h-9 p-2 rounded-lg outline-none shadow-field transition-all duration-200",
          open
            ? "border border-border-field-focus bg-surface-focus-selected"
            : "border-[0.5px] border-border-field-default bg-surface-field-default hover:border-border-field-hover"
        )}
      >
        <Text
          as="span"
          variant={open ? "bodyTertiary" : "body"}
          className="flex-1 text-left truncate"
        >
          {t(`contracts.proposals.recipients.role.${role}`)}
        </Text>
        <IconContainer as="span" size="5" color="secondary">
          {open ? <ChevronUp /> : <ChevronDown />}
        </IconContainer>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-[200px] p-1">
        {RECIPIENT_ROLES.map((option) => {
          const Icon = ROLE_ICONS[option];
          const selected = option === role;
          const unavailable =
            option === ProposalRecipientRole.SIGNER &&
            !selected &&
            !!signerUnavailableReason;
          return (
            <DropdownMenuItem
              key={option}
              disabled={unavailable}
              onSelect={() => onChange(option)}
              className="gap-2 h-auto min-h-9 pl-2 pr-3 py-2 focus:bg-surface-hover-soft"
              data-test={`${dataTest}-${option}`}
            >
              <IconContainer as="span" size="5" color="secondary">
                <Icon />
              </IconContainer>
              <span className="flex flex-col flex-1 min-w-0">
                <Text as="span" variant="body" className="truncate">
                  {t(`contracts.proposals.recipients.role.${option}`)}
                </Text>
                {unavailable ? (
                  <Text as="span" variant="smBodySecondary">
                    {signerUnavailableReason}
                  </Text>
                ) : null}
              </span>
              {selected ? (
                <IconContainer as="span" size="5" color="primary">
                  <Check />
                </IconContainer>
              ) : null}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
